import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Stack,
  Alert,
  InputAdornment,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import integrationService from '../../services/integrationService'
import { getErrorMessage } from '../../utils/errorHandlers'

const SMS_EVENT_TYPES = [
  { value: 'OTP_VERIFICATION', label: 'Login / OTP Verification' },
  { value: 'ORDER_PLACED', label: 'Order Received / Placed' },
  { value: 'PAYMENT_RECEIVED', label: 'Payment Confirmation' },
  { value: 'CART_ABANDONED', label: 'Abandoned Cart Recovery' },
  { value: 'ORDER_CONFIRMED', label: 'Order Confirmed' },
  { value: 'ORDER_SHIPPED', label: 'Order Shipped / Dispatched' },
  { value: 'ORDER_DELIVERED', label: 'Order Delivered' },
  { value: 'ORDER_CANCELLED', label: 'Order Cancelled' },
  { value: 'PAYMENT_FAILED', label: 'Payment Failed' },
  { value: 'STOCK_ALERT', label: 'Stock Alert' },
  { value: 'CUSTOMER_WELCOME', label: 'Customer Welcome' },
  { value: 'CUSTOM', label: 'Custom SMS' },
]

export interface SMSTemplateRow {
  id: string
  name: string
  code: string
  event_type: string
  content?: string
  sms_message?: string
  dlt_template_id?: string
  template_config?: Record<string, any>
  is_active: boolean
  variables: string[]
  created_at?: string
  updated_at?: string
}

interface TemplateFormState {
  name: string
  code: string
  event_type: string
  content: string
  dlt_template_id: string
  variables: string
  is_active: boolean
}

const emptyForm: TemplateFormState = {
  name: '',
  code: '',
  event_type: 'CUSTOM',
  content: '',
  dlt_template_id: '',
  variables: '',
  is_active: true,
}

interface Props {
  onSelectForTest?: (templateId: string) => void
}

const SMSTemplatesTab: React.FC<Props> = ({ onSelectForTest }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [templates, setTemplates] = useState<SMSTemplateRow[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<TemplateFormState>(emptyForm)
  const [saving, setSaving] = useState(false)

  const loadTemplates = async () => {
    setLoading(true)
    try {
      const data = await integrationService.getTemplates({ channel: 'sms' })
      const list = Array.isArray(data) ? data : (data as any)?.items || []
      setTemplates(list as SMSTemplateRow[])
    } catch (err: any) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to load SMS templates'), { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTemplates()
  }, [])

  const openCreateDialog = () => {
    setEditingId(null)
    setForm(emptyForm)
    setDialogOpen(true)
  }

  const openEditDialog = (t: SMSTemplateRow) => {
    setEditingId(t.id)
    const dltId = t.dlt_template_id || t.template_config?.dlt_template_id || ''
    const body = t.content || t.sms_message || ''
    setForm({
      name: t.name || '',
      code: t.code || '',
      event_type: t.event_type || 'CUSTOM',
      content: body,
      dlt_template_id: dltId,
      variables: (t.variables || []).join(', '),
      is_active: t.is_active ?? true,
    })
    setDialogOpen(true)
  }

  const closeDialog = () => setDialogOpen(false)

  const handleSave = async () => {
    if (!form.name.trim() || !form.code.trim() || !form.content.trim()) {
      enqueueSnackbar('Name, Code, and Content are required', { variant: 'warning' })
      return
    }

    setSaving(true)
    const variables = form.variables
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)

    const dltId = form.dlt_template_id.trim() || undefined
    const templateConfig = {
      ...(dltId ? { dlt_template_id: dltId } : {}),
    }

    try {
      if (editingId) {
        await integrationService.updateTemplate(editingId, {
          name: form.name.trim(),
          content: form.content.trim(),
          sms_message: form.content.trim(),
          dlt_template_id: dltId,
          template_config: templateConfig,
          variables,
          is_active: form.is_active,
        } as any)
        enqueueSnackbar('SMS Template updated successfully', { variant: 'success' })
      } else {
        await integrationService.createTemplate({
          name: form.name.trim(),
          code: form.code.trim(),
          event_type: form.event_type,
          channel: 'SMS' as any,
          content: form.content.trim(),
          sms_message: form.content.trim(),
          dlt_template_id: dltId,
          template_config: templateConfig,
          variables,
          is_active: form.is_active,
        } as any)
        enqueueSnackbar('SMS Template created successfully', { variant: 'success' })
      }
      closeDialog()
      await loadTemplates()
    } catch (err: any) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to save SMS template'), { variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete template "${name}"?`)) return
    try {
      await integrationService.deleteTemplate(id)
      enqueueSnackbar('SMS Template deleted', { variant: 'success' })
      await loadTemplates()
    } catch (err: any) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to delete template'), { variant: 'error' })
    }
  }

  const handleCopyDLTFormat = (content: string) => {
    // Convert {{variable}} to {#var#} standard format for Indian telecom DLT portals
    const dltFormatted = content.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, '{#var#}')
    navigator.clipboard.writeText(dltFormatted)
    enqueueSnackbar('DLT format ({#var#}) copied to clipboard!', { variant: 'success' })
  }

  const handleCopyText = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    enqueueSnackbar(`${label} copied to clipboard!`, { variant: 'info' })
  }

  const insertVariable = (varName: string) => {
    setForm((prev) => ({
      ...prev,
      content: prev.content + `{{${varName}}}`,
      variables: prev.variables
        ? prev.variables.includes(varName)
          ? prev.variables
          : `${prev.variables}, ${varName}`
        : varName,
    }))
  }

  const getCharStats = (text: string) => {
    // Detect non-ASCII unicode (which reduces single SMS length from 160 to 70 chars)
    const isUnicode = /[^\u0000-\u007F]/.test(text)
    const length = text.length
    const limitPerSms = isUnicode ? 70 : 160
    const credits = length === 0 ? 0 : Math.ceil(length / limitPerSms)
    return { length, isUnicode, limitPerSms, credits }
  }

  const formStats = getCharStats(form.content)

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2.5,
          border: '1px solid #E2E8F0',
          bgcolor: '#FFFFFF',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', fontSize: '1.05rem' }}>
            DLT Registered SMS Templates
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748B', mt: 0.3 }}>
            Manage SMS message text and their corresponding 19-digit DLT Content Template IDs required by Indian telecom operators.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          sx={{
            bgcolor: '#0C2340',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            '&:hover': { bgcolor: '#1E293B' },
          }}
        >
          New SMS Template
        </Button>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: '1px dashed #CBD5E1' }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No SMS templates found.
          </Typography>
          <Button variant="outlined" size="small" onClick={openCreateDialog} sx={{ mt: 1 }}>
            Create First SMS Template
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2.5, border: '1px solid #E2E8F0' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#F8FAFC' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem' }}>Template Name & Code</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem' }}>Event Trigger</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem' }}>DLT Template ID</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem' }}>Message Text Preview</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((t) => {
                const dltId = t.dlt_template_id || t.template_config?.dlt_template_id
                const content = t.content || t.sms_message || ''
                const stats = getCharStats(content)

                return (
                  <TableRow key={t.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                        {t.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748B', fontFamily: 'monospace' }}>
                        {t.code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t.event_type}
                        size="small"
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          bgcolor: '#F1F5F9',
                          color: '#334155',
                          borderRadius: 1.5,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {dltId ? (
                        <Tooltip title="Click to copy DLT Template ID">
                          <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: '14px !important', color: '#16A34A !important' }} />}
                            label={dltId}
                            size="small"
                            onClick={() => handleCopyText(dltId, 'DLT ID')}
                            sx={{
                              bgcolor: '#F0FDF4',
                              color: '#166534',
                              fontWeight: 700,
                              fontFamily: 'monospace',
                              cursor: 'pointer',
                              border: '1px solid #BBF7D0',
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          icon={<WarningIcon sx={{ fontSize: '14px !important', color: '#D97706 !important' }} />}
                          label="DLT ID Missing"
                          size="small"
                          sx={{
                            bgcolor: '#FFFBEB',
                            color: '#92400E',
                            fontWeight: 600,
                            border: '1px solid #FDE68A',
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#1E293B',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontFamily: 'inherit',
                          fontSize: '0.85rem',
                        }}
                      >
                        {content}
                      </Typography>
                      <Typography variant="caption" sx={{ color: stats.isUnicode ? '#D97706' : '#64748B', mt: 0.3, display: 'block' }}>
                        {stats.length} chars • {stats.credits} SMS {stats.isUnicode ? '(Unicode)' : '(GSM-7)'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t.is_active ? 'Active' : 'Disabled'}
                        size="small"
                        sx={{
                          bgcolor: t.is_active ? '#ECFDF5' : '#F1F5F9',
                          color: t.is_active ? '#059669' : '#64748B',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Copy in DLT Registration Format ({#var#})">
                          <IconButton size="small" onClick={() => handleCopyDLTFormat(content)}>
                            <CopyIcon fontSize="small" sx={{ color: '#64748B' }} />
                          </IconButton>
                        </Tooltip>
                        {onSelectForTest && (
                          <Tooltip title="Send Test SMS with this template">
                            <IconButton size="small" onClick={() => onSelectForTest(t.id)}>
                              <SendIcon fontSize="small" sx={{ color: '#2563EB' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit Template & DLT ID">
                          <IconButton size="small" onClick={() => openEditDialog(t)}>
                            <EditIcon fontSize="small" sx={{ color: '#0C2340' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(t.id, t.name)}>
                            <DeleteIcon fontSize="small" sx={{ color: '#EF4444' }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#0F172A', pb: 1 }}>
          {editingId ? 'Edit SMS Template & DLT ID' : 'Create New SMS Template'}
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
          <Alert severity="info" icon={<InfoIcon fontSize="inherit" />} sx={{ borderRadius: 2 }}>
            <strong>DLT Requirement in India:</strong> Telecom operators (TRAI) mandate that SMS gateways deliver messages
            only if the registered <strong>DLT Content Template ID</strong> matches the exact approved wording.
          </Alert>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Template Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              required
              placeholder="e.g. Order Placed SMS"
            />
            <TextField
              label="Template Code"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              fullWidth
              required
              disabled={!!editingId}
              placeholder="e.g. order_placed_sms"
              helperText={editingId ? 'Code cannot be changed once created' : 'Unique identifier for backend triggers'}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label="Event Trigger"
              value={form.event_type}
              onChange={(e) => setForm({ ...form, event_type: e.target.value })}
              fullWidth
            >
              {SMS_EVENT_TYPES.map((et) => (
                <MenuItem key={et.value} value={et.value}>
                  {et.label} ({et.value})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="DLT Content Template ID"
              value={form.dlt_template_id}
              onChange={(e) => setForm({ ...form, dlt_template_id: e.target.value })}
              fullWidth
              placeholder="e.g. 1407168854321987654"
              helperText="19-digit template ID from telecom DLT portal (Vilpower/Jio/Airtel/Smartping)"
              InputProps={{
                endAdornment: form.dlt_template_id ? (
                  <InputAdornment position="end">
                    <CheckCircleIcon sx={{ color: '#16A34A', fontSize: 20 }} />
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Stack>

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                Message Text Template
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: formStats.isUnicode ? '#D97706' : formStats.length > 160 ? '#2563EB' : '#16A34A',
                }}
              >
                {formStats.length} characters • {formStats.credits} SMS {formStats.isUnicode ? '(Unicode limit: 70)' : '(GSM limit: 160)'}
              </Typography>
            </Stack>

            <TextField
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              multiline
              rows={4}
              fullWidth
              required
              placeholder="Dear {{customer_name}}, your order {{order_id}} has been received..."
            />

            {/* Quick Variable Insert Pills */}
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 0.5, fontWeight: 600 }}>
                Click to insert dynamic variable:
              </Typography>
              <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                {[
                  'customer_name',
                  'order_id',
                  'amount',
                  'otp_code',
                  'tracking_url',
                  'company_name',
                  'checkout_url',
                  'payment_id',
                ].map((v) => (
                  <Chip
                    key={v}
                    label={`{{${v}}}`}
                    size="small"
                    onClick={() => insertVariable(v)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: '#F1F5F9',
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      '&:hover': { bgcolor: '#E2E8F0' },
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* DLT Format Helper */}
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155', display: 'block' }}>
                    DLT Portal Template Text:
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748B', fontFamily: 'monospace' }}>
                    {form.content ? form.content.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, '{#var#}') : 'None'}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CopyIcon sx={{ fontSize: 14 }} />}
                  onClick={() => handleCopyDLTFormat(form.content)}
                  disabled={!form.content}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Copy for DLT Portal
                </Button>
              </Stack>
            </Box>
          </Box>

          <TextField
            label="Variables (comma-separated)"
            value={form.variables}
            onChange={(e) => setForm({ ...form, variables: e.target.value })}
            fullWidth
            placeholder="customer_name, order_id, amount"
            helperText="Dynamic fields populated when triggering this SMS"
          />

          <FormControlLabel
            control={
              <Switch
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                color="primary"
              />
            }
            label={form.is_active ? 'Active (Ready for sending)' : 'Disabled'}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={closeDialog} sx={{ textTransform: 'none', color: '#64748B' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              bgcolor: '#0C2340',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#1E293B' },
            }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : editingId ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default SMSTemplatesTab
